/**
 * Login Page with Google OAuth and Magic Link
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { googleLogin } from "@/store/slices/authSlice";
import { getAndClearRedirectUrl } from "@/utils/storage";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { MagicLinkForm } from "@/components/auth/MagicLinkForm";
import { Header } from "@/components/layout/Header";
import { AlertCircle } from "lucide-react";
import logo from "@/assets/logo.png";
import { logger } from "@/utils/logger";
import { SEOPresets } from "@/components/SEO";

export function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated } = useAppSelector(
    (state) => state.auth
  );

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Try to get redirect URL from localStorage first (set by ProtectedRoute)
      // Fall back to 'from' state for backward compatibility, then default to /meeting-simulator
      const redirectUrl = getAndClearRedirectUrl();
      logger.log("[Login] Redirecting after auth:", {
        redirectUrl,
        from: (location.state as any)?.from?.pathname,
      });
      const from =
        redirectUrl ||
        (location.state as any)?.from?.pathname ||
        "/meeting-simulator";
      navigate(from, {
        replace: true,
      });
    }
  }, [isAuthenticated, navigate, location]);
  const handleGoogleSuccess = async (
    credentialResponse: CredentialResponse
  ) => {
    logger.log("Google OAuth Success:", credentialResponse);
    if (!credentialResponse.credential || !credentialResponse.clientId) {
      logger.error("No credential or clientId received from Google");
      return;
    }
    try {
      const result = await dispatch(
        googleLogin({
          provider: "google",
          process: "login",
          token: {
            id_token: credentialResponse.credential,
            client_id: credentialResponse.clientId,
          },
        })
      ).unwrap();
      logger.log("Login successful:", result);
      // Don't navigate here - let the useEffect above handle the redirect
      // This way it will use the 'from' state if available
    } catch (err: any) {
      logger.log("=== LOGIN ERROR DEBUG ===");
      logger.log("Full error:", JSON.stringify(err, null, 2));
      if (err.response) {
        logger.log("Response data:", err.response.data);
        logger.log("Response status:", err.response.status);
        logger.log("Response headers:", err.response.headers);
      }
      logger.log("=== END DEBUG ===");
    }
  };
  const handleGoogleError = () => {
    logger.error("Google OAuth Error");
  };
  return (
    <div className="min-h-screen flex flex-col">
      {SEOPresets.login}
      {/* Header persistente com botão "Back to Home" - NÃO mostra botão Login (estamos NA página de login) */}
      <Header showBackButton showLogout={false} />

      {/* Conteúdo da página de login */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <img src={logo} alt="Logo" className="h-12 w-auto" />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold">Welcome!</CardTitle>
              <CardDescription className="text-base mt-2">
                Sign in to continue
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Magic Link Form - FIRST */}
            <div className="flex flex-col items-center">
              <MagicLinkForm />
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  or
                </span>
              </div>
            </div>

            {/* Google OAuth - SECOND */}
            <div className="flex flex-col items-center justify-center space-y-3">
              <div className="flex items-center justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  logo_alignment="left"
                />
              </div>

              {loading && (
                <p className="text-sm text-muted-foreground animate-pulse">
                  Signing in...
                </p>
              )}
            </div>

            <div className="text-center text-xs text-muted-foreground pt-2">
              By signing in, you agree to our{" "}
              <a
                href="https://globalcoders.app/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://globalcoders.app/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground transition-colors"
              >
                Privacy Policy
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
